import fetchBHApi from "@helper/fetchBHApi";
import { getGTMData, getMetaData } from "@helper/getMetaData";
import useSwrApi from "@hooks/useSwrApi";
import type { BHBasePageProps } from "@routes/BHBasePage";
import BHBasePage from "@routes/BHBasePage";
import { type PodcastProcessedResponse } from "@transformer/usePodcastResponse";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ClientLoaderFunctionArgs, useParams } from "react-router";

import PodcastMetaData from "./PodcastMetaData";
import PodcastNew from "./PodcastNew";

export function meta() {
  const sectionName = "podcasts";
  return [getMetaData(PodcastMetaData(sectionName).metaTags)];
}

export default function PodcastPage() {
  const podcastApi = "podcasts?size=8";
  const { data } = useSwrApi<PodcastProcessedResponse[]>(podcastApi);
  const { level1: id } = useParams();
  const [podcasts, setPodcasts] = useState<PodcastProcessedResponse[]>();
  const [size, setSize] = useState(8);
  const [playing, setPlaying] = useState<
    PodcastProcessedResponse | undefined
  >();

  useEffect(() => {
    const fetchData = async () => {
      if (!data || data.length === 0) return;
      let nowPlaying = false;
      try {
        const podcast: PodcastProcessedResponse[] = id
          ? await fetchBHApi<
              PodcastProcessedResponse[]
            > // eslint-disable-next-line prettier/prettier
            (
              `podcasts/${id}`, // eslint-disable-next-line prettier/prettier
              "GET", // eslint-disable-next-line prettier/prettier
            )
          : data;

        setPlaying(podcast[0]);
        nowPlaying = id ? false : true;
      } catch (error) {
        nowPlaying = true;
        setPlaying(data[0]);
      }

      const updatedPodcasts = data.map((podcast, index) =>
        index === 0
          ? { ...podcast, nowPlaying: nowPlaying ? true : false }
          : podcast,
      );
      setPodcasts(updatedPodcasts);
    };

    void fetchData();
  }, [id, data]);

  const handleNowPlaying = useMemo(
    () => (id: string) => {
      setPodcasts(
        (prevValue) =>
          prevValue &&
          prevValue.map((video) => ({
            ...video,
            nowPlaying: video.id === id,
          })),
      );
    },
    [setPodcasts],
  );

  const handleOnClickThumbnail = useCallback(
    (props: PodcastProcessedResponse) => {
      // eslint-disable-next-line react/prop-types
      const { id } = props;
      handleNowPlaying(id);
      setPlaying(props);
    },
    [handleNowPlaying, setPlaying],
  );

  const handleLoadMorePodcasts = async () => {
    const morePodcasts = await fetchBHApi<PodcastProcessedResponse[]>(
      `podcasts?size=${size}`,
      "GET",
    );
    setPodcasts((prevPodcast) => {
      const combinedPodcasts = [...(prevPodcast || []), ...morePodcasts];

      const uniquePodcastsMap = new Map();
      combinedPodcasts.forEach((podcast) =>
        uniquePodcastsMap.set(podcast.id, podcast),
      );
      const uniquePodcasts = Array.from(uniquePodcastsMap.values());
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return uniquePodcasts;
    });
    setSize((prevValue) => prevValue + 8);

    // This avoids GTM push.
    return 0;
  };

  const sectionName = "podcasts";
  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;
  const basePageProps: BHBasePageProps = {
    uniqueSectionName: sectionName,
    pageAdTargetValue: pageAdTargetValue,
    hideNavigationInMobile: true,
  };

  const renderPodcastPage = () => {
    return (
      <PodcastNew
        playing={playing}
        podcasts={podcasts}
        sectionName={sectionName}
        pageAdTargetValue={pageAdTargetValue}
        handleLoadMorePodcasts={handleLoadMorePodcasts}
        handleOnClickThumbnail={handleOnClickThumbnail}
      />
    );
  };

  return <BHBasePage {...basePageProps}>{renderPodcastPage()}</BHBasePage>;
}

import {
  fetchContext,
  ResponseType,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";

export function loader(): Promise<TRouteWithRedirect<object | null, string>> {
  const sectionName = "podcasts";
  const { gaData } = PodcastMetaData(sectionName);

  return Promise.resolve({
    type: ResponseType.SUCCESS,
    statusCode: 200,
    payload: null,
    gtm: getGTMData(gaData),
  });
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<PodcastProcessedResponse, string>
  >;
}
